
import { Component } from '@angular/core';

import { ChangePasswordDialogComponent } from '@components/change-password-dialog/change-password-dialog.component';

import { GroupDto } from '@models/authorizations/group.model';
import { SubscriberDto } from '@models/users/subscriber.dto';

import { AccountService } from '@services/account.service';
import { SubscriberService } from '@services/subscriber.service';

import {
  DialogService,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';

interface BarcodeBar {
  x: number;
  width: number;
}

interface BarcodeData {
  value: string;
  width: number;
  bars: BarcodeBar[];
}

@Component({
  selector: 'ebs-user-info',
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss'],
})
export class UserInfoComponent {
  public get user(): SubscriberDto {
    return this.accountService.user!;
  }

  public get group(): GroupDto {
    return this.accountService.group!;
  }

  public isEditingPhone: boolean = false;
  public showPrivacy: boolean = false;
  public showBarcode: boolean = false;

  private readonly code39Patterns: Record<string, string> = {
    '0': 'nnnwwnwnn',
    '1': 'wnnwnnnnw',
    '2': 'nnwwnnnnw',
    '3': 'wnwwnnnnn',
    '4': 'nnnwwnnnw',
    '5': 'wnnwwnnnn',
    '6': 'nnwwwnnnn',
    '7': 'nnnwnnwnw',
    '8': 'wnnwnnwnn',
    '9': 'nnwwnnwnn',

    A: 'wnnnnwnnw',
    B: 'nnwnnwnnw',
    C: 'wnwnnwnnn',
    D: 'nnnnwwnnw',
    E: 'wnnnwwnnn',
    F: 'nnwnwwnnn',
    G: 'nnnnnwwnw',
    H: 'wnnnnwwnn',
    I: 'nnwnnwwnn',
    J: 'nnnnwwwnn',

    K: 'wnnnnnnww',
    L: 'nnwnnnnww',
    M: 'wnwnnnnwn',
    N: 'nnnnwnnww',
    O: 'wnnnwnnwn',
    P: 'nnwnwnnwn',
    Q: 'nnnnnnwww',
    R: 'wnnnnnwwn',
    S: 'nnwnnnwwn',
    T: 'nnnnwnwwn',

    U: 'wwnnnnnnw',
    V: 'nwwnnnnnw',
    W: 'wwwnnnnnn',
    X: 'nwnnwnnnw',
    Y: 'wwnnwnnnn',
    Z: 'nwwnwnnnn',

    '-': 'nwnnnnwnw',
    '.': 'wwnnnnwnn',
    ' ': 'nwwnnnwnn',
    '$': 'nwnwnwnnn',
    '/': 'nwnwnnnwn',
    '+': 'nwnnnwnwn',
    '%': 'nnnwnwnwn',
    '*': 'nwnnwnwnn',
  };

  private cachedBarcodeValue: string = '';
  private cachedBarcodeData: BarcodeData | null = null;

  public get barcodeData(): BarcodeData | null {
    const value = String(this.user.externalCode || '')
      .trim()
      .toUpperCase();

    if (value === this.cachedBarcodeValue) {
      return this.cachedBarcodeData;
    }

    this.cachedBarcodeValue = value;
    this.cachedBarcodeData = this.createBarcode(value);

    return this.cachedBarcodeData;
  }

  constructor(
    public dialogService: DialogService,
    private accountService: AccountService,
    private subscriberService: SubscriberService,
  ) {}

  private createBarcode(value: string): BarcodeData | null {
    if (!value) {
      return null;
    }

    if (
      value.includes('*') ||
      !Array.from(value).every(
        (character) => this.code39Patterns[character] !== undefined,
      )
    ) {
      return null;
    }

    const narrowWidth = 2;
    const wideWidth = 5;
    const characterGap = 2;
    const quietZone = 20;

    const bars: BarcodeBar[] = [];
    const encodedValue = `*${value}*`;

    let currentX = quietZone;

    for (const character of encodedValue) {
      const pattern = this.code39Patterns[character];

      for (let index = 0; index < pattern.length; index++) {
        const elementWidth =
          pattern[index] === 'w' ? wideWidth : narrowWidth;

        if (index % 2 === 0) {
          bars.push({
            x: currentX,
            width: elementWidth,
          });
        }

        currentX += elementWidth;
      }

      currentX += characterGap;
    }

    return {
      value,
      width: currentX + quietZone - characterGap,
      bars,
    };
  }

  changePassword() {
    let dialogRef: DynamicDialogRef = this.dialogService.open(
      ChangePasswordDialogComponent,
      {
        header: 'Cambio Password',

        maximizable: false,
        draggable: false,
        closable: true,
        closeOnEscape: false,

        width: 'calc(100vw - 40px)',

        style: {
          'max-width': '560px',
          'max-height': 'calc(100vh - 40px)',
          'border-radius': '16px',
          overflow: 'hidden',
        },

        contentStyle: {
          'overflow-y': 'auto',
          padding: '24px',
        },
      },
    );

    dialogRef.onClose.subscribe((result) => {
      if (!result) {
        return;
      }

      this.accountService.changePassword(result).subscribe();
    });
  }

  editUser() {
    this.subscriberService.editSubscriber(this.user).subscribe(() => {
      this.isEditingPhone = false;
    });
  }

  closePrivacyDialog() {
    this.showPrivacy = false;
  }
}
